import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  FormControl,
  FormLabel,
  Box,
  Divider,
  SimpleGrid,
  useToast,
  Badge,
  Image,
  Wrap,
  WrapItem,
  Tooltip,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { MultaPolicia, policiaMunicipalApi } from '../../services/policiaMunicipalApi';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

interface ModalCalificarMultaProps {
  isOpen: boolean;
  onClose: () => void;
  multa: MultaPolicia | null;
  onCalificar: () => void;
}

export default function ModalCalificarMulta({ isOpen, onClose, multa, onCalificar }: ModalCalificarMultaProps) {
  const [estatus, setEstatus] = useState('');
  const [monto, setMonto] = useState('');
  const [loading, setLoading] = useState(false);
  const [evidencias, setEvidencias] = useState<any[]>([]);
  const [imagenSeleccionada, setImagenSeleccionada] = useState<string | null>(null);
  const toast = useToast();
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure();
  
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  useEffect(() => {
    if (multa) {
      setEstatus(multa.estatus || 'PENDIENTE');
      setMonto(multa.monto?.toString() || '0');
      fetchEvidencias();
    }
  }, [multa]);

  const fetchEvidencias = async () => {
    if (!multa?.id) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/multas-transito/${multa.id}/evidencias`);
      if (response.ok) {
        const data = await response.json();
        setEvidencias(data);
      }
    } catch (error) {
      console.error('Error al cargar evidencias:', error);
    }
  };

  const abrirImagen = (url: string) => {
    const urlCompleta = url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}${url}`;
    setImagenSeleccionada(urlCompleta);
    onImageOpen();
  };

  const handleCalificar = async () => {
    if (!multa) return;

    const montoNumerico = Number(monto);
    if (montoNumerico < 0) {
      toast({
        title: 'Error de validación',
        description: 'El monto no puede ser negativo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await policiaMunicipalApi.updateMulta(multa.id!, {
        ...multa,
        estatus,
        monto: montoNumerico,
        calificado: true
      });

      toast({
        title: 'Infracción calificada',
        description: `La infracción ${multa.folio} ha sido calificada correctamente`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      onCalificar();
      onClose();
    } catch (error) {
      toast({
        title: 'Error al calificar',
        description: error instanceof Error ? error.message : 'Error desconocido',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!multa) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader 
          fontSize="2xl" 
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white" 
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          borderTopRadius="md"
          position="relative"
        >
          Calificar Infracción - {multa.folio}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody p={6}>
          <Tabs variant="enclosed" colorScheme="red">
            <TabList>
              <Tab>Información General</Tab>
              <Tab>Evidencias</Tab>
              <Tab>Ubicación</Tab>
              <Tab>Garantía</Tab>
              <Tab>Calificación</Tab>
            </TabList>
            
            <TabPanels>
              <TabPanel>
                <VStack spacing={6} align="stretch">
            
            {/* Información de la Infracción */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4}>
                Información de la Infracción
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Folio</Text>
                  <Text>{multa.folio}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Fecha</Text>
                  <Text>{multa.fechaInfraccion?.split('T')[0]}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Lugar</Text>
                  <Text>{multa.lugarInfraccion || 'No especificado'}</Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Información del Vehículo */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4}>
                Información del Vehículo
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Placa</Text>
                  <Text>{multa.numeroPlaca}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Estado Emisor</Text>
                  <Text>{multa.entidad || multa.estadoEmisorPlaca || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Tipo de Vehículo</Text>
                  <Text>{multa.tipoVehiculo}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Marca</Text>
                  <Text>{multa.marca || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Modelo</Text>
                  <Text>{multa.modeloVehiculo || multa.modelo || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Color</Text>
                  <Text>{multa.color || 'N/A'}</Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Información del Conductor */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4}>
                Información del Conductor
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Nombre Completo</Text>
                  <Text>
                    {multa.ciudadano || 
                     (multa.nombreConductor && multa.apellidosConductor 
                      ? `${multa.nombreConductor} ${multa.apellidosConductor}`
                      : 'No registrado')
                    }
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Teléfono</Text>
                  <Text>{multa.telefonoConductor || 'N/A'}</Text>
                </Box>
                <Box gridColumn={{ base: 1, md: "1 / -1" }}>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Domicilio</Text>
                  <Text>{multa.domicilioConductor || 'N/A'}</Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Información de la Infracción */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4}>
                Detalles de la Infracción
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Artículo Violado</Text>
                  <Text>{multa.articuloViolado || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Fracción</Text>
                  <Text>{multa.fraccion || 'N/A'}</Text>
                </Box>
                <Box gridColumn={{ base: 1, md: "1 / -1" }}>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Descripción de la Conducta</Text>
                  <Text>{multa.descripcion || multa.descripcionConducta || 'N/A'}</Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Información del Oficial */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4}>
                Oficial que Levantó la Infracción
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Nombre del Oficial</Text>
                  <Text>{multa.nombreOficial || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">Grado</Text>
                  <Text>{multa.gradoOficial || 'N/A'}</Text>
                </Box>
              </SimpleGrid>
            </Box>

                </VStack>
              </TabPanel>
              
              {/* Tab de Evidencias */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Text fontWeight="bold" fontSize="lg" color="#5b182b">
                    Evidencias Fotográficas ({evidencias.length})
                  </Text>
                  {evidencias.length > 0 ? (
                    <Wrap spacing={4}>
                      {evidencias.map((evidencia, idx) => (
                        <WrapItem key={idx}>
                          <Tooltip label="Clic para ampliar">
                            <Image
                              src={evidencia.url.startsWith('http') ? evidencia.url : `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}${evidencia.url}`}
                              alt={`Evidencia ${idx + 1}`}
                              boxSize="150px"
                              objectFit="cover"
                              borderRadius="md"
                              border="2px solid #bb8c52"
                              cursor="pointer"
                              onClick={() => abrirImagen(evidencia.url)}
                              _hover={{ transform: 'scale(1.05)', borderColor: '#5b182b' }}
                              transition="all 0.2s"
                            />
                          </Tooltip>
                        </WrapItem>
                      ))}
                    </Wrap>
                  ) : (
                    <Text color="gray.500">No hay evidencias fotográficas disponibles</Text>
                  )}
                </VStack>
              </TabPanel>
              
              {/* Tab de Ubicación */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Text fontWeight="bold" fontSize="lg" color="#5b182b">
                    Ubicación de la Infracción
                  </Text>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Dirección</Text>
                    <Text>{multa.lugarInfraccion || 'No especificado'}</Text>
                  </Box>
                  {multa.latitud && multa.longitud && isLoaded ? (
                    <Box h="400px" w="100%" borderRadius="md" overflow="hidden">
                      <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={{ lat: Number(multa.latitud), lng: Number(multa.longitud) }}
                        zoom={16}
                      >
                        <Marker position={{ lat: Number(multa.latitud), lng: Number(multa.longitud) }} />
                      </GoogleMap>
                    </Box>
                  ) : (
                    <Text color="gray.500">No hay coordenadas disponibles para mostrar el mapa</Text>
                  )}
                </VStack>
              </TabPanel>
              
              {/* Tab de Garantía */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Text fontWeight="bold" fontSize="lg" color="#5b182b">
                    Información de Garantía
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontWeight="semibold" fontSize="sm" color="gray.700">Tipo de Garantía</Text>
                      <Badge bg="#bb8c52" color="white" p={2} borderRadius="md">
                        {multa.tipoGarantia || 'No especificado'}
                      </Badge>
                    </Box>
                    
                    {/* Información de Placa */}
                    {(multa.placaGarantia || multa.numeroPlaca) && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">Placa de Garantía</Text>
                        <Text>{multa.placaGarantia || multa.numeroPlaca}</Text>
                      </Box>
                    )}
                    
                    {/* Información de Vehículo */}
                    {multa.numeroSerieVIN && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">Número de Serie/VIN</Text>
                        <Text>{multa.numeroSerieVIN}</Text>
                      </Box>
                    )}
                    {multa.numeroMotor && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">Número de Motor</Text>
                        <Text>{multa.numeroMotor}</Text>
                      </Box>
                    )}
                    {(multa.corralon || multa.corralónAsignado) && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">Corralón Asignado</Text>
                        <Text>{multa.corralon || multa.corralónAsignado}</Text>
                      </Box>
                    )}
                    {multa.ubicacionAseguramiento && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">Ubicación de Aseguramiento</Text>
                        <Text>{multa.ubicacionAseguramiento}</Text>
                      </Box>
                    )}
                    
                    {/* Órdenes de Grúa */}
                    {multa.ordenGrua && multa.ordenGrua.length > 0 && (
                      <Box gridColumn={{ base: 1, md: "1 / -1" }}>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Órdenes de Grúa ({multa.ordenGrua.length})</Text>
                        <VStack spacing={2} align="stretch">
                          {multa.ordenGrua.map((orden, idx) => (
                            <HStack key={idx} p={3} bg="gray.50" borderRadius="md" justify="space-between">
                              <VStack align="start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium">{orden.observaciones || `Orden ${idx + 1}`}</Text>
                                <Text fontSize="xs" color="gray.600">
                                  {orden.fechaHora ? new Date(orden.fechaHora).toLocaleString('es-ES') : 'Sin fecha'}
                                </Text>
                              </VStack>
                              <Button
                                size="sm"
                                bg="#bb8c52"
                                color="white"
                                _hover={{ bg: "#a67c47" }}
                                onClick={() => {
                                  if (orden.id && multa.id) {
                                    const url = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/multas-transito/${multa.id}/orden-grua/${orden.id}/view`;
                                    window.open(url, '_blank');
                                  }
                                }}
                              >
                                Ver PDF
                              </Button>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    )}
                    
                    {/* Información de Licencia */}
                    {multa.tipoLicencia && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">Tipo de Licencia</Text>
                        <Text>{multa.tipoLicencia}</Text>
                      </Box>
                    )}
                    {multa.categoriaLicencia && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">Categoría</Text>
                        <Text>{multa.categoriaLicencia}</Text>
                      </Box>
                    )}
                    {multa.entidadEmisoraLicencia && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">Entidad Emisora</Text>
                        <Text>{multa.entidadEmisoraLicencia}</Text>
                      </Box>
                    )}
                  </SimpleGrid>
                </VStack>
              </TabPanel>
              
              {/* Tab de Calificación */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Box bg="#5b182b0a" p={4} borderRadius="md" border="2px solid #5b182b">
                    <Text fontWeight="bold" fontSize="lg" color="#5b182b" mb={4}>
                      Calificación del Juez Municipal
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <FormControl isRequired>
                        <FormLabel color="#5b182b">Estatus</FormLabel>
                        <Select
                          value={estatus}
                          onChange={(e) => setEstatus(e.target.value)}
                          bg="white"
                        >
                          <option value="PENDIENTE">Pendiente</option>
                          <option value="PAGADO">Pagado</option>
                          <option value="CANCELADO">Cancelado</option>
                        </Select>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel color="#5b182b">Monto de la Sanción ($)</FormLabel>
                        <Input
                          type="number"
                          value={monto}
                          onChange={(e) => setMonto(e.target.value)}
                          bg="white"
                          min="0"
                          step="0.01"
                        />
                      </FormControl>

                      {/* <FormControl>
                        <FormLabel color="#5b182b">Estado</FormLabel>
                        <Badge 
                          bg="#bb8c52" 
                          color="white" 
                          p={2} 
                          borderRadius="md"
                          fontSize="sm"
                        >
                          Se marcará como CALIFICADA
                        </Badge>
                      </FormControl> */}
                    </SimpleGrid>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        
        {/* Modal para ampliar imagen */}
        <Modal isOpen={isImageOpen} onClose={onImageClose} size="xl" isCentered>
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="transparent" boxShadow="none" maxW="90vw" maxH="90vh">
            <ModalCloseButton color="white" bg="#bb8c52" _hover={{ bg: '#a67c47' }} size="lg" />
            <ModalBody p={0} display="flex" justifyContent="center" alignItems="center" minH="90vh">
              {imagenSeleccionada && (
                <Image
                  src={imagenSeleccionada}
                  alt="Evidencia ampliada"
                  maxW="100%"
                  maxH="90vh"
                  objectFit="contain"
                  borderRadius="md"
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            bg="#5b182b"
            color="white"
            _hover={{ bg: "#691f30" }}
            onClick={handleCalificar}
            isLoading={loading}
            loadingText="Calificando..."
          >
            Calificar Infracción
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
